'use client';

// Microsoft Clarity loader + typed helpers.
// Project ID is hardcoded because it is public (appears in the <script src> URL
// rendered on every page); there is no secret to protect.

const CLARITY_PROJECT_ID = 'weii23zrvb';
const ENABLE_IN_DEV = false;

type ConsentV2 = { ad_Storage: 'granted' | 'denied'; analytics_Storage: 'granted' | 'denied' };

type ClarityFn = ((...args: unknown[]) => void) & { q?: unknown[] };

declare global {
  interface Window {
    clarity?: ClarityFn;
    __clarityLoaded?: boolean;
  }
}

function ensureQueueShim() {
  if (window.clarity) return;
  const q: unknown[] = [];
  const shim = ((...args: unknown[]) => {
    q.push(args);
  }) as ClarityFn;
  shim.q = q;
  window.clarity = shim;
}

export function injectClarity(): Promise<void> {
  return new Promise((resolve) => {
    const isAllowed = process.env.NODE_ENV === 'production' || ENABLE_IN_DEV;
    if (!isAllowed || window.__clarityLoaded) {
      resolve();
      return;
    }
    window.__clarityLoaded = true;
    ensureQueueShim();

    const s = document.createElement('script');
    s.async = true;
    s.src = `https://www.clarity.ms/tag/${CLARITY_PROJECT_ID}`;
    s.onload = () => resolve();
    s.onerror = () => resolve();
    (document.head || document.documentElement).appendChild(s);
  });
}

export function grantClarityConsent(c: Partial<ConsentV2> = {}) {
  const payload: ConsentV2 = {
    ad_Storage: c.ad_Storage ?? 'denied',
    analytics_Storage: c.analytics_Storage ?? 'granted',
  };
  window.clarity?.('consentv2', payload);
}

export function denyClarityConsent() {
  window.clarity?.('consentv2', { ad_Storage: 'denied', analytics_Storage: 'denied' });
}

export function clarityEvent(name: string) {
  window.clarity?.('event', name);
}

export function claritySet(key: string, value: string) {
  window.clarity?.('set', key, value);
}

export function clarityIdentify(customId: string, sessionId?: string, pageId?: string, friendlyName?: string) {
  window.clarity?.('identify', customId, sessionId, pageId, friendlyName);
}
